import { Transform, Type } from 'class-transformer'
import {
  ArrayMaxSize,
  ArrayMinSize,
  ArrayUnique,
  IsArray,
  IsBoolean,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  MaxLength,
  Min,
  ValidateNested,
} from 'class-validator'
import 'reflect-metadata'

export const V2_PRODUCT_MAX_INGREDIENTS = 4
export const V2_PRODUCT_DEFAULT_REENTRY_INTERVAL = 48

export class SaveV2ProductIngredient {
  @IsInt()
  @IsPositive()
  ingredientId!: number

  @IsNumber({ allowInfinity: false, allowNaN: false })
  @Min(0)
  concentration!: number

  @IsInt()
  @IsPositive()
  concentrationUnitId!: number
}

export class SaveV2ProductCountry {
  @IsInt()
  @IsPositive()
  countryId!: number

  @Transform(({ value }: { value: unknown }) => (typeof value === 'string' ? value.trim() : value))
  @IsString()
  @IsNotEmpty()
  @MaxLength(100)
  manufacturer!: string

  @IsOptional()
  @IsString()
  @MaxLength(100)
  identificationNumber?: string

  @IsOptional()
  @IsString()
  @MaxLength(255)
  referenceProduct?: string

  @IsOptional()
  @IsInt()
  @Min(0)
  reentryInterval?: number | null
}

export class SaveV2Product {
  @Transform(({ value }: { value: unknown }) => (typeof value === 'string' ? value.trim() : value))
  @IsString()
  @IsNotEmpty()
  @MaxLength(255)
  name!: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(90)
  pesticideType!: string

  @IsInt()
  @IsPositive()
  useFormId!: number

  @IsInt()
  @IsPositive()
  formulationId!: number

  @IsBoolean()
  isActive!: boolean

  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(V2_PRODUCT_MAX_INGREDIENTS)
  @ArrayUnique((value: SaveV2ProductIngredient) => value.ingredientId)
  @ValidateNested({ each: true })
  @Type(() => SaveV2ProductIngredient)
  ingredients!: SaveV2ProductIngredient[]

  @IsArray()
  @ArrayMinSize(1)
  @ArrayUnique((value: SaveV2ProductCountry) => value.countryId)
  @ValidateNested({ each: true })
  @Type(() => SaveV2ProductCountry)
  countries!: SaveV2ProductCountry[]
}

export const V2_PRODUCT_SORT_COLUMNS = [
  'name',
  'type',
  'formulation',
  'ingredients',
  'countries',
  'status',
  'manufacturer',
  'identificationNumber',
  'referenceProduct',
  'reentryInterval',
] as const
export type V2ProductSort = (typeof V2_PRODUCT_SORT_COLUMNS)[number]
export const V2_PRODUCT_STATUSES = ['all', 'active', 'inactive'] as const
export type V2ProductStatus = (typeof V2_PRODUCT_STATUSES)[number]

export class V2ProductQuery {
  @IsOptional()
  @IsString()
  @MaxLength(255)
  search?: string

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  ingredientId?: number

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  countryId?: number

  @IsOptional()
  @IsString()
  @MaxLength(100)
  manufacturer?: string

  @IsOptional()
  @IsString()
  @MaxLength(90)
  pesticideType?: string

  @IsOptional()
  @IsIn(V2_PRODUCT_STATUSES)
  status: V2ProductStatus = 'all'

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page = 1

  @IsOptional()
  @IsIn(V2_PRODUCT_SORT_COLUMNS)
  sort: V2ProductSort = 'name'

  @IsOptional()
  @IsIn(['asc', 'desc'])
  direction: 'asc' | 'desc' = 'asc'
}

export interface V2ProductOption {
  id: number
  name: string
}
export interface V2ProductIngredientDto {
  ingredientId: number
  name: string
  concentration: number
  concentrationUnitId: number
  concentrationUnit: string
}
export interface V2ProductCountryDto {
  countryId: number
  name: string
  isoCode?: string | null
  manufacturer: string
  identificationNumber: string | null
  referenceProduct: string | null
  reentryInterval: number | null
}
export interface V2ProductDto {
  id: number
  tempId?: string | null
  name: string
  pesticideType: string | null
  useFormId: number | null
  useForm: string | null
  formulationId: number | null
  formulation: string | null
  isActive: boolean
  ingredients: V2ProductIngredientDto[]
  countries: V2ProductCountryDto[]
}
/** `country` is the registration of the filtered country, when the search is restricted to one. */
export interface V2ProductRow {
  product: V2ProductDto
  country?: V2ProductCountryDto
}
export interface V2ProductPage {
  data: V2ProductRow[]
  currentPage: number
  resultsCount: number
  pageCount: number
}
export interface V2ProductOptions {
  pesticideTypes: string[]
  useForms: V2ProductOption[]
  formulations: V2ProductOption[]
  ingredients: V2ProductOption[]
  countries: V2ProductOption[]
  concentrationUnits: V2ProductOption[]
  manufacturers: string[]
  specifications: V2ProductOption[]
}

export const V2_CLOSEST_PRODUCTS_SHOWN = 8
/** From the closest to the farthest: same ingredients and concentrations, same ingredients, some ingredients. */
export type V2ProductMatchLevel = 'identical' | 'sameIngredients' | 'sharedIngredients'
export interface V2ClosestProductDto {
  product: V2ProductDto
  level: V2ProductMatchLevel
  sharedIngredients: number
  /** One of the two products is declared as the reference product of the other. */
  isReference: boolean
  specificationIds: number[]
}
export interface V2ProductSpecificationUseDto {
  id: number
  specificationId: number
  specification: string
  cropType: string | null
  country: string
  maxRate: number
  rateUnit: string | null
  passageNumber: number | null
  preHarvestInterval: number | null
}
export interface V2ProductDetailsDto {
  product: V2ProductDto
  /** Catalogue product id of each reference product name (upper case) that names another product. */
  referenceProductIds: Record<string, number>
  closestProductsCount: number
  closestProducts: V2ClosestProductDto[]
  specifications: V2ProductSpecificationUseDto[]
}
